import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, formatApiError } from "@/auth";
import { Mail, KeyRound, XCircle, Loader2, Hourglass, PauseCircle, ChevronDown } from "lucide-react";
import { fmtLocal } from "@/lib/time";

export const ORDER_STATUS = {
  pending: ["En attente", "text-zinc-300 border-zinc-600"],
  processing: ["En cours de traitement", "text-sky-400 border-sky-500/40"],
  payment_confirmed: ["Paiement confirmé", "text-amber-400 border-amber-500/40"],
  activating: ["Activation…", "text-orange-400 border-orange-500/40"],
  activated: ["Activée", "text-emerald-400 border-emerald-500/40"],
  rejected: ["Rejetée", "text-red-400 border-red-500/40"],
  expired: ["Expirée", "text-zinc-500 border-zinc-700"],
};
const CATEGORIES = [["", "Toutes les demandes"], ["pending", "En attente"], ["processing", "En cours de traitement"], ["activated", "Licences activées"], ["rejected", "Demandes rejetées"], ["expired", "Expirées"]];
const PLANS = ["1M", "3M", "6M", "12M"];
const METHODS = [["", "Moyen de paiement"], ["airtel", "Airtel Money"], ["orange", "Orange Money"], ["binance", "Binance Pay"], ["visa", "Visa / Carte"]];

export function OrderStatusPill({ s }) {
  const [l, c] = ORDER_STATUS[s] || [s, "text-zinc-400 border-zinc-700"];
  return <span data-testid={`order-status-${s}`} className={`text-[10px] px-2 py-0.5 rounded-full border font-mono-tech uppercase ${c}`}>{l}</span>;
}

export function OrderHistory({ items = [] }) {
  return (
    <ol className="space-y-1 border-l border-zinc-800 pl-3" data-testid="order-history">
      {items.map((h, i) => (
        <li key={i} className="text-[11px] text-zinc-400">
          <span className="text-zinc-500 font-mono-tech">{fmtLocal(h.at)}</span> · <OrderStatusPill s={h.status} /> · {h.by}{h.note ? ` — ${h.note}` : ""}
        </li>
      ))}
    </ol>
  );
}

export function LicenseOrdersAdmin() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const load = () => axios.get(`${API}/admin/license-orders`, { params: filter ? { status: filter } : {} }).then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { axios.post(`${API}/admin/license-orders/mark-seen`).then(() => window.dispatchEvent(new Event("lhs-orders-seen"))).catch(() => {}); }, []);

  return (
    <div className="card-lhs p-5 space-y-4" data-testid="license-orders-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display font-bold uppercase tracking-wider text-sm">Gestion des demandes de licences</h2>
        <div className="flex flex-wrap gap-1">
          {CATEGORIES.map(([k, l]) => (
            <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`orders-filter-${k || "all"}`}
              className={`px-2.5 py-1 rounded text-[10px] uppercase font-mono-tech border transition-colors ${filter === k ? "bg-orange-500 text-black border-orange-500" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="space-y-3 max-h-[70vh] overflow-y-auto scroll-thin">
        {rows.length === 0 && <div className="text-sm text-zinc-500 italic">Aucune demande.</div>}
        {rows.map(o => <OrderRow key={o.id} o={o} onChange={load} />)}
      </div>
    </div>
  );
}

function OrderRow({ o, onChange }) {
  const [price, setPrice] = useState(o.agreed_price ?? o.amount ?? "");
  const [method, setMethod] = useState(o.payment_method || "");
  const [plan, setPlan] = useState(o.plan || "3M");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);
  const closed = ["activated", "rejected", "expired", "activating"].includes(o.status);

  const run = async (fn) => {
    setBusy(true); setErr(null);
    try { await fn(); onChange(); } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  const fields = () => ({ agreed_price: price === "" ? null : Number(price), payment_method: method || null });
  const patch = (extra) => run(() => axios.patch(`${API}/admin/license-orders/${o.id}`, { ...fields(), ...extra }));
  const reject = () => {
    const reason = window.prompt("Motif du rejet (envoyé au technicien) :");
    if (reason && reason.trim()) patch({ status: "rejected", reason: reason.trim() });
  };
  const activate = () => {
    if (!window.confirm(`Activer la licence ${plan} pour ${o.email} ?`)) return;
    run(async () => {
      await axios.patch(`${API}/admin/license-orders/${o.id}`, fields());
      await axios.post(`${API}/admin/license-orders/${o.id}/activate`, { plan });
    });
  };
  const msg = encodeURIComponent(`Bonjour ${o.username}, concernant votre demande ${o.reference} (${o.software}${o.plan ? " " + o.plan : ""})`);

  return (
    <div className="rounded-lg border border-zinc-800 bg-[#0C0C10] p-4 text-xs space-y-3" data-testid={`order-row-${o.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono-tech text-orange-400 text-sm">{o.reference}</span>
            <OrderStatusPill s={o.status} />
            <span className="text-zinc-300">{o.software}{o.plan ? ` · ${o.plan}` : ""}</span>
            {o.amount != null && <span className="text-white font-semibold" data-testid={`order-amount-${o.id}`}>{o.amount} {o.currency}</span>}
          </div>
          <div className="text-zinc-400">{o.username} · {o.email} {o.user_id ? "" : <span className="text-amber-400">· sans compte</span>}</div>
          <div className="text-zinc-500">Demande du {fmtLocal(o.created_at)}</div>
          {o.message && <div className="text-zinc-500 italic">« {o.message} »</div>}
          {o.reject_reason && <div className="text-red-400">Motif : {o.reject_reason}</div>}
          {o.expires_at && <div className="text-zinc-500">{o.plan} · activée le {fmtLocal(o.activated_at)} · expire le {fmtLocal(o.expires_at)}</div>}
        </div>
        <a href={`mailto:${o.email}?subject=${encodeURIComponent("Demande " + o.reference)}&body=${msg}`} data-testid={`order-contact-${o.id}`}
          className="px-2.5 py-1.5 rounded-lg border border-zinc-700 hover:border-orange-500 inline-flex items-center gap-1 transition-colors"><Mail className="w-3.5 h-3.5" /> Contacter le client</a>
      </div>
      {!closed && (
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid={`order-price-${o.id}`} type="number" min={0} step="any" placeholder="Prix convenu" value={price} onChange={e => setPrice(e.target.value)} className="input-lhs !w-28 !py-1.5" />
          <select data-testid={`order-method-${o.id}`} value={method} onChange={e => setMethod(e.target.value)} className="input-lhs !w-40 !py-1.5">
            {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select data-testid={`order-plan-${o.id}`} value={plan} onChange={e => setPlan(e.target.value)} className="input-lhs !w-24 !py-1.5">
            {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <Btn testid={`order-processing-${o.id}`} onClick={() => patch({ status: "processing" })} icon={Hourglass}>En cours de traitement</Btn>
          <Btn testid={`order-activate-${o.id}`} onClick={activate} icon={KeyRound} primary>Activer la licence</Btn>
          <Btn testid={`order-reject-${o.id}`} onClick={reject} icon={XCircle} danger>Rejeter la demande</Btn>
          <Btn testid={`order-hold-${o.id}`} onClick={() => patch({ status: "pending" })} icon={PauseCircle}>En attente</Btn>
          {busy && <Loader2 className="w-4 h-4 animate-spin text-orange-400" />}
        </div>
      )}
      {o.status === "activated" && <div className={o.email_sent ? "text-emerald-400" : "text-amber-400"}>{o.email_sent ? "E-mail de confirmation envoyé" : "E-mail non envoyé"}</div>}
      {err && <div className="text-red-400" data-testid={`order-err-${o.id}`}>{err}</div>}
      <button type="button" onClick={() => setOpen(!open)} data-testid={`order-history-toggle-${o.id}`} className="text-[11px] text-zinc-500 hover:text-orange-400 inline-flex items-center gap-1">
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} /> Historique ({(o.history || []).length})
      </button>
      {open && <OrderHistory items={o.history} />}
    </div>
  );
}

function Btn({ children, onClick, icon: Icon, primary, danger, testid }) {
  const cls = primary ? "btn-orange" : danger ? "border border-red-500/40 text-red-400 hover:bg-red-500/10" : "border border-zinc-700 hover:border-orange-500";
  return (
    <button type="button" data-testid={testid} onClick={onClick} className={`px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1 transition-colors ${cls}`}>
      <Icon className="w-3.5 h-3.5" /> {children}
    </button>
  );
}
